import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import BankView from './BankView';
import withPermission from '../../../utils/withPermission';
import { useAuth } from '../../../context/AuthContext';
import TransactionList from './TransactionList';
import { Container, Card, Tabs, Tab, Button } from 'react-bootstrap';
import { BiPencil } from "react-icons/bi";
import { BsChevronLeft } from "react-icons/bs"; 
import BankModal from './BankModal';

const WithTenanatPermission = withPermission(({ children }) => children, ['tenant-list']);
const WithContactPermission = withPermission(({ children }) => children, ['contact-list']);
const WithDocumentPermission = withPermission(({ children }) => children, ['document-list']);

const BankDetail = () => {
    const { id } = useParams(); // Get bank ID from the URL
    const navigate = useNavigate();
    const location = useLocation();
    
    const [bankDetails, setBankDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('transactionList');
    const { permissions, companySettings } = useAuth();
    const didFetchRef = useRef(false); // Use ref to track whether the API has been called
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get("activeTab") ||"transactionList"
    const [selectedBank, setSelectedBank] = useState(null);
        const [showModal, setShowModal] = useState(false);
        const [banks, setBanks] = useState([]);
        const [countries, setCountries] = useState([]);
        const [closingBalance, setClosingBalance] = useState(null);

    const fetchBankDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/bank-accounts/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            setBankDetails(response.data); // Set the fetched data to state
        } catch (error) {
            console.error('Error fetching bank details:', error);
        }
    };
    const fetchClosingBalance = async (bankName) => {
        if (!bankName) return;
        try {
            const Api = process.env.REACT_APP_WEBZASH_URL;
            const apiKey = process.env.REACT_APP_API_KEY;

            // 1. Find Ledger ID by matching bank name
            const ledgersResponse = await axios.get(`${Api}/api/ledgers/index`, {
                headers: { 'x-api-key': apiKey, 'Accept': 'application/json' }
            });

            const ledgerList = ledgersResponse.data?.data || [];
            const matchingLedger = ledgerList.find(item => 
                item.Ledger?.name?.toLowerCase() === bankName.toLowerCase()
            );

            if (!matchingLedger) {
                console.warn(`No matching ledger found for bank: ${bankName}`);
                return;
            }

            const ledgerId = matchingLedger.Ledger.id;

            // 2. Fetch ledger statement for that ID
            const response = await axios.get(`${Api}/api/reports/ledgerstatement`, {
                params: { ledger_id: ledgerId },
                headers: { 'x-api-key': apiKey, 'Accept': 'application/json' },
            });
            if (response.data && response.data.closing_balance) {
                setClosingBalance(response.data.closing_balance);
            }
        } catch (error) {
            console.error('Error fetching closing balance:', error);
        }
    };
    const fetchCountries = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;
    
            const response = await axios.get(`${backendUrl}/api/countries`, {
                headers: { Authorization: `Bearer ${token}` ,
                    'Tenant-Origin': origin,
            
            },
                skipLoader: true,
            });
    
            let countryList = response.data.Data;
    
            // Ensure country with ID 231 (AED) appears first
            // countryList = countryList.sort((a, b) => (a.id === 231 ? -1 : b.id === 231 ? 1 : 0));
    
            setCountries(countryList);
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };
    const editBank = async (bankId, updatedData) => {
       
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;

            const response = await axios.put(`${backendUrl}/api/bank-accounts/${bankId}`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
            });
            const updatedBank = response.data?.account;

            if (updatedBank) {
                setBankDetails((prevLead) => ({
                    ...prevLead,
                    ...updatedBank, // Merge updated data
                }));
                setBanks((prevBanks) =>
                    prevBanks.map((bank) =>
                        bank.id === updatedBank.id ? updatedBank : bank
                    )
                );
            } else {
                console.error('Update failed, no data returned.');
            }

            setShowModal(false);
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors; // Return validation errors
            } else {
                console.error('Error updating bank:', error);
            }
        }
    };

    useEffect(() => {
        if (id) {
            fetchBankDetails(); 
        }
    }, [id]); 

    useEffect(() => {
        if (bankDetails?.name) {
            fetchClosingBalance(bankDetails.name);
        }
    }, [bankDetails?.name]);

    const getExchangeRate = (bank, targetCurrencyCode) => {
        if (!targetCurrencyCode) return null;

        // 1. If target is USD, strictly check Company Settings/Settings first as requested
        if (targetCurrencyCode === 'USD') {
             if (companySettings?.usd_to_aed) return parseFloat(companySettings.usd_to_aed);
             const localRate = localStorage.getItem('exchange_rate');
             if (localRate) return parseFloat(localRate);
        }
        
        // 2. Fallback to Bank's custom exchange rates array
        if (bank?.exchange_rates && Array.isArray(bank.exchange_rates)) {
            const rateObj = bank.exchange_rates.find(r => r.currency?.currency === targetCurrencyCode);
            if (rateObj) return parseFloat(rateObj.exchange_rate);
        }

        // 3. Fallback to Bank's deprecated exchange_rate field
        if (bank?.exchange_rate && targetCurrencyCode === 'USD') {
             return parseFloat(bank.exchange_rate);
        }

        return targetCurrencyCode === 'USD' ? 3.67 : null;
    };

    const convertAEDToBankCurrency = (ledgerBalance, bank) => {
        if (!bank || !ledgerBalance) return null;
        
        // Robust detection of bank's base currency
        const bankCurrency = (bank.base_currency?.currency || bank.currency?.currency || bank.currency || 'AED').toString().trim().toUpperCase();
        
        let amount = parseFloat(ledgerBalance.amount || 0);
        if (ledgerBalance.dc === 'C') amount = -amount;

        // If ledger is AED and bank is AED, no conversion
        if (bankCurrency === 'AED') {
            const formatted = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return `${amount >= 0 ? 'Dr' : 'Cr'} ${formatted}`;
        }

        // Ledger entries from Webzash are ALWAYS in AED.
        // 1. Check if bank has a specific rate for AED
        const aedRateObj = bank.exchange_rates?.find(r => 
            (r.currency?.currency || '').toString().trim().toUpperCase() === 'AED'
        );

        if (aedRateObj) {
            const rate = parseFloat(aedRateObj.rate || aedRateObj.exchange_rate || 0);
            if (rate > 0) {
                // In bank specific rates, it's usually AED * rate = USD
                amount = amount * rate;
            }
        } else {
            // 2. Fallback to global settings (Division: AED / 3.67)
            const globalRate = parseFloat(companySettings?.usd_to_aed || localStorage.getItem('exchange_rate') || '3.67');
            if (globalRate > 0 && bankCurrency === 'USD') {
                amount = amount / globalRate;
            }
        }

        const formatted = Math.abs(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        
        return `${amount >= 0 ? 'Dr' : 'Cr'} ${formatted}`;
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const activeTabParam = queryParams.get("activeTab") || "transactionList"; // Default to 'tenantInfo'
        setActiveTab(activeTabParam); // Set the active tab based on URL parameter
      }, [location.search]);
    const handleBackClick = () => {
        navigate(-1);
    };

    // Handle tab click to change active tab
    const handleTabClick = (tab) => {
        setActiveTab(tab);
        navigate(`${location.pathname}?activeTab=${tab}`);
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    {/* Page Title */}
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <h4 className="mb-sm-0 d-flex align-items-center first-title">
                                    <BsChevronLeft
                                        className='back-button1'
                                        onClick={() => navigate(-1)}
                                    />
                                    <span>Accounts</span>
                                </h4>                                
                                <div className="page-title-right br-font">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item"><Link to={`/bank/details/${id}?activeTab=bankInfo`}>
                                            {bankDetails?.name || " Bank Details"}
                                        </Link></li>
                                        <li className="breadcrumb-item active">Details</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="p-6 max-w-7xl mx-auto">
                    <>
                            <Card className="mb-4 shadow-lg rounded-3 p-2 border-0 card-bg ">
                                {/* Header Section with Name and Edit Icon */}
                                <div className="d-flex justify-content-between align-items-center">
                                    <Card.Title className="fw-bold font-size-14 text-primary">{bankDetails?.name}</Card.Title>
                                    <BiPencil
                                        className="text-primary hover-effect2 edit-pencil"
                                        onClick={() => {
                                            setSelectedBank(bankDetails); // Store organisation details
                                            setShowModal(true); // Open modal
                                        }} />
                                </div>
                                <Card.Body className="pt-3">
                                    <div className="row">
                                        {/* Left Section */}
                                        <div className="col-md-6 ">
                                            <div className="mb-3">
                                                <span className="text-muted d-block card-label"><strong>Account Type:</strong></span>
                                                <span className=" text-muted card-value">{bankDetails?.type || '-'}</span>
                                            </div>
                                            <div className="mb-3">
                                                <span className="text-muted d-block card-label"><strong>Branch Name:</strong></span>
                                                <span className=" text-muted card-value">{bankDetails?.branch_name || '-'}</span>
                                            </div>
                                        </div>

                                        {/* Right Section */}
                                        <div className="col-md-4 ">
                                            <div className="mb-3">
                                                <span className="text-muted d-block card-label"><strong>Opening Balance:</strong></span>
                                                <span className="fw-semibold text-primary card-value">
                                                    {bankDetails?.currency?.currency} {bankDetails?.opening_balance || '0.00'}
                                                </span>
                                            </div>
                                            <div className="mb-3">
                                                <span className="text-muted d-block card-label"><strong>Running Balance:</strong></span>
                                                <span className="fw-bold text-success card-value font-size-15">
                                                    {bankDetails?.currency?.currency} {convertAEDToBankCurrency(closingBalance, bankDetails) || '0.00'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                            {showModal && (
                                <BankModal
                                    showModal={showModal}
                                    handleClose={() => setShowModal(false)}
                                    bankData={selectedBank} // Pass data here
                                    editBank={editBank}
                                    banks={banks}
                                    bankId={id}
                                    countries={countries}
                                    fetchCountries={fetchCountries}
                                   
                                />
                            )}


                        </>
                    <div className="row">
                    <div className="col-xl-12 col-lg-8">
                                <div className="card">
                                    {/* Nav tabs */}
                                    <ul className="nav nav-tabs nav-tabs-custom justify-content-start pt-2 font-size-13" role="tablist">
                                    <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'transactionList' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('transactionList')}
                                                role="tab"
                                            >
                                                TransactionList
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'bankDetails' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('bankDetails')}
                                                role="tab"
                                            >
                                                Bank Details
                                            </a>
                                        </li>
                                        
                                    </ul>
                                    <div className="tab-content p-4">
                                       
                                        {activeTab === "bankDetails" && bankDetails && (
                                            <div className="tab-pane active" id="instacne-list" role="tabpanel">
                                                <div className="p-3">
                                                <BankView bankData={bankDetails} setBankDetails={setBankDetails}/>
                                                </div>
                                            </div>
                                        )}
                                         {activeTab === "transactionList" && bankDetails && (
                                            <div className="tab-pane active" id="instacne-list" role="tabpanel">
                                                <div className="p-3">
                                                 <TransactionList bankDetails={bankDetails} accountId={bankDetails?.id} fetchBankDetails={fetchBankDetails} setBankDetails={setBankDetails}/>
                                                </div>
                                            </div>
                                        )}
                                        </div>
                                    </div>
                                    </div>
                      
                    </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankDetail;
